import React, { useContext, useEffect, useRef, useState, Fragment } from "react";
import MapContext from "../MapContext";
import { MousePosition, ScaleLine } from "ol/control";
import "../Map.css";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { createStringXY, toStringHDMS } from "ol/coordinate.js";
import { Card } from "@progress/kendo-react-layout";
import { Button } from "@progress/kendo-react-buttons";
import WKIDIcon from "../../icons/wkid.svg";
import { useSelector } from "react-redux";
import { Input } from "@progress/kendo-react-inputs";
import "./ScaleOverlays.css";
import { TbZoomPan } from "react-icons/tb";

const ScaleOverlays = () => {
    const mapConfig = useSelector((state) => state.configData.mapConfig);
    const { map } = useContext(MapContext);
    const scaleBar = useRef();
    const mousePositionElement = useRef();
    const mousePositionRef = useRef();
    const firstRender = useRef(true);
    const [open, setOpen] = useState(true);
    const wkids = [
        {
            id: mapConfig.map.projection,
            text: mapConfig.map.projection.split(":")[1],
            proj: mapConfig.map.projection,
        },
        { id: "lat/lng", text: "4326 Lat/Long", proj: "EPSG:4326" },
        // { id: "ddm", text: "4326 DDM", proj: "EPSG:4326" },
        { id: "dms", text: "4326 DMS", proj: "EPSG:4326" },
        // { id: "x/y", text: "3857 X/Y", proj: "EPSG:3857" },
    ];

    const [selectedOption, setSelectedOption] = useState({
        id: mapConfig.map.projection,
        text: mapConfig.map.projection.split(":")[1],
        proj: mapConfig.map.projection,
    });
    const selectedRef = useRef({
        id: mapConfig.map.projection,
        text: mapConfig.map.projection.split(":")[1],
        proj: mapConfig.map.projection,
    });

    const handleChange = (e) => {
        selectedRef.current = e.target.value;
        setSelectedOption(e.target.value);
        mousePositionRef.current.setProjection(e.target.value.proj);
        if (selectedRef.current.id === "dms") {
            mousePositionRef.current.setCoordinateFormat(toStringHDMS);
        } else if (selectedRef.current.id === "lat/lng") {
            mousePositionRef.current.setCoordinateFormat(createStringXY(6));
        } else if (selectedRef.current.id === mapConfig.map.projection) {
            mousePositionRef.current.setCoordinateFormat(createStringXY(2));
        }
    };

    const decideCoordinateFormat = () => {
        let format;
        if (selectedRef.current.id === "dms") {
            format = toStringHDMS;
        } else if (selectedRef.current.id === "lat/lng") {
            format = createStringXY(4);
        } else if (selectedRef.current.id === mapConfig.map.projection) {
            format = createStringXY(2);
        }
        return format;
    };

    const handleOpen = () => {
        setOpen(!open);
    };

    useEffect(() => {
        if (!map) return;
        // Create scale control and add to map
        const scaleControl = new ScaleLine({
            bar: true,
            steps: 2,
            units: "imperial",
            maxWidth: 200,
            minWidth: 75,
            target: scaleBar.current,
        });
        mousePositionRef.current = new MousePosition({
            coordinateFormat: createStringXY(2),
            projection: selectedRef.current.proj,
            className: "custom-mouse-position",
            target: mousePositionElement.current,
        });
        map.addControl(mousePositionRef.current);
        map.addControl(scaleControl);

        return () => {
            map.removeControl(mousePositionRef.current);
            map.removeControl(scaleControl);
        };
    }, [map]);

    useEffect(() => {
        if (firstRender.current === true) {
            firstRender.current = false;
        } else {
            if (open === true) {
                mousePositionRef.current = new MousePosition({
                    coordinateFormat: decideCoordinateFormat(),
                    projection: selectedRef.current.proj,
                    className: "custom-mouse-position",
                    target: mousePositionElement.current,
                });
                map.addControl(mousePositionRef.current);
            } else if (open === false) {
                map.removeControl(mousePositionRef.current);
            }
        }
    }, [open]);

    return (
        <Fragment>
            <div className="scale-container">
                <Card className={`custom-control${open ? " open" : ""}`}>
                    <Button onClick={handleOpen}>
                        <img src={WKIDIcon} alt="WKID" />
                    </Button>
                    {open ? (
                        <div className="coordinate-picker">
                            <DropDownList
                                style={{
                                    width: "fit-content",
                                }}
                                data={wkids}
                                dataItemKey="id"
                                textField="text"
                                value={selectedOption}
                                onChange={(e) => handleChange(e)}
                            />
                            <div className="coords">
                                <div ref={mousePositionElement} />
                            </div>
                        </div>
                    ) : (
                        <div />
                    )}
                </Card>
                <ScaleInput />
                <div ref={scaleBar} className={"scale-bar"} />
            </div>
        </Fragment>
    );
};

export default ScaleOverlays;

export const ScaleInput = () => {
    const { map } = useContext(MapContext);
    const prevScale = useRef();
    const [mapScale, setMapScale] = useState();
    const scaleTextRef = useRef();
    const [open, setOpen] = useState(false);

    const handleKeyDown = (e) => {
        const scale = Number(scaleTextRef.current);
        if (e.key === "Enter" && scale < 200000000000 && scale > 0) {
            const res = mapScale / 96 / 12;
            map.getView().animate({ resolution: res });
        } else if (e.key === "Enter") {
            setMapScale(prevScale.current);
        }
    };

    const handleTextChange = (e) => {
        const newScale = e.value;
        setMapScale(newScale);
        scaleTextRef.current = newScale;
    };

    useEffect(() => {
        if (!map) return;
        const res = map.getView().getResolution();
        const scale = Math.round(res * 96 * 12);
        prevScale.current = scale;
        setMapScale(scale);
        const trackResolution = () => {
            const res = map.getView().getResolution();
            if (res !== prevScale.current) {
                const scale = Math.round(res * 96 * 12);
                prevScale.current = scale;
                setMapScale(scale);
            }
        };
        map.on("moveend", () => {
            trackResolution();
        });
        return () => {
            map.un("moveend", trackResolution);
        };
    }, [map]);

    return (
        <Card className="scale-input">
            <Button
                onClick={() => setOpen(!open)}
                style={{
                    padding: "4px",
                    alignItems: "center",
                    justifyContent: "center",
                }}>
                <TbZoomPan style={{ width: "28px", height: "28px" }} />
            </Button>
            {open ? (
                <>
                    <span className="scale-input-label">Scale{"   "} 1:</span>
                    <Input
                        style={{ width: "fit-content", maxWidth: "85px" }}
                        onKeyDown={handleKeyDown}
                        onChange={handleTextChange}
                        value={mapScale}
                    />
                </>
            ) : (
                <div />
            )}
        </Card>
    );
};
